/**
 * MSI3 / AcuityDrill one-time learner onboarding.
 * learnerType student|clinician; optional orgId for school students. No US checkbox.
 */
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Alert, Box, Stack, TextField, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";
import { CLINICAL_HUD5_TEACHING } from "features/clinicalHud5/clinicalHud5WorkspaceConfig";
import { saveMsi3LearnerProfile } from "../msi3TeachingCallables";

/**
 * @param {{ refresh?: () => void, productTitle?: string, productTagline?: string }} props
 */
export default function Msi3LearnerOnboarding({
  refresh,
  productTitle = CLINICAL_HUD5_TEACHING.productTitle,
  productTagline = CLINICAL_HUD5_TEACHING.productTagline,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      learnerType: "",
      orgId: "",
    },
    mode: "onChange",
  });

  const learnerType = watch("learnerType");

  const onSubmit = async (values) => {
    if (submitting) return;
    if (values.learnerType !== "student" && values.learnerType !== "clinician") {
      setError("Choose whether you are a student or a clinician.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const orgTrimmed = (values.orgId || "").trim();
      await saveMsi3LearnerProfile({
        learnerType: values.learnerType,
        orgId:
          values.learnerType === "student" && orgTrimmed
            ? orgTrimmed
            : null,
      });
      if (typeof refresh === "function") {
        refresh();
      }
    } catch (err) {
      setError(
        err?.message || "Could not save your profile. Please try again.",
      );
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640, mx: "auto", py: 6, px: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Welcome to {productTitle}
      </Typography>
      {productTagline ? (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {productTagline}
        </Typography>
      ) : null}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Tell us how you will use teaching cases so we can tailor access and
        school options.
      </Typography>
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          I am a…
        </Typography>
        <Controller
          name="learnerType"
          control={control}
          render={({ field }) => (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <SelectableChoiceCard
                title="Student"
                description="Medical student or trainee using cases for learning."
                selected={field.value === "student"}
                disabled={submitting}
                onClick={() => field.onChange("student")}
              />
              <SelectableChoiceCard
                title="Clinician"
                description="Practicing clinician using cases for continuing education."
                selected={field.value === "clinician"}
                disabled={submitting}
                onClick={() => field.onChange("clinician")}
              />
            </Stack>
          )}
        />

        {learnerType === "student" ? (
          <Controller
            name="orgId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="School org id (optional)"
                fullWidth
                disabled={submitting}
                helperText="If your school gave you an organization id, enter it here."
                sx={{ mb: 2 }}
              />
            )}
          />
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        <LoadingButton
          type="submit"
          loading={submitting}
          disabled={!learnerType}
        >
          Continue
        </LoadingButton>
      </Box>
    </Box>
  );
}
